import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Button,
  TextField,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import StorageIcon from "@mui/icons-material/Storage";
import { toast } from "react-toastify";
import axios from "axios";

import { API_HOST } from "../../api_utils/index.js";
import { useDispatch } from "react-redux";
import { getAllModels } from "../../Redux/Actions/customTableActions";

const AIModelGenerator = ({ project, onModelGenerated }) => {
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [isCopied, setIsCopied] = useState(false);
  const [suggestedTables, setSuggestedTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [fieldEditDialog, setFieldEditDialog] = useState(false);
  const [currentEditField, setCurrentEditField] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  let dispatch = useDispatch();

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.warning("Please enter a description for your system", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    setSuggestedTables([]);
    setSelectedTables({});

    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setGenerationProgress((prev) => {
        if (prev >= 90) {
          return 90; // Stop at 90% until API responds
        }
        return prev + 10;
      });
    }, 200);

    try {
      // Get user token from localStorage
      const userInfo = localStorage.getItem("userInfo");
      if (!userInfo) {
        throw new Error("User not authenticated. Please login again.");
      }

      const user = JSON.parse(userInfo);
      const token = user.token;

      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      // Make API call
      const response = await axios.post(
        `${API_HOST}ai`,
        {
          userQuestion: prompt,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log("response.data.data", response.data);

      // Clear progress interval and set to 100%
      clearInterval(progressInterval);
      setGenerationProgress(100);

      // Check if response has the expected structure and validate data
      // Replace this section in handleGenerate function:
      if (
        response.data &&
        response.data.data &&
        response.data.data.tables &&
        Array.isArray(response.data.data.tables)
      ) {
        // Validate and clean the data - UPDATED FOR NEW API STRUCTURE
        const validatedTables = response.data.data.tables.map((table) => ({
          ...table,
          Table_Name: table.tablename || "Unnamed Table", // Fixed: tablename -> Table_Name
          description: table.description || "No description provided",
          Fields: Array.isArray(table.fields)
            ? table.fields.map((field) => ({
                Field_Name: field.fieldname || "Unnamed Field", // Fixed: fieldname -> Field_Name
                Field_Type: field.field_type || "String", // Fixed: field_type -> Field_Type
                description: field.description || "No description provided",
              }))
            : [],
        }));

        setSuggestedTables(validatedTables);

        // Initialize selected tables state
        const initialSelected = {};
        validatedTables.forEach((table, index) => {
          initialSelected[index] = false;
        });
        setSelectedTables(initialSelected);

        toast.success("Database tables generated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
      } else if (response.data && response.data.data) {
        throw new Error(response.data.data);
      } else {
        throw new Error("Invalid response format from AI service");
      }
    } catch (error) {
      console.error("Error generating tables:", error);

      // Clear progress interval
      clearInterval(progressInterval);
      setGenerationProgress(0);

      // Handle different types of errors
      let errorMessage = "Failed to generate tables. Please try again.";

      if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          errorMessage = "Authentication failed. Please login again.";
        } else if (error.response.status === 403) {
          errorMessage =
            "Access denied. You don't have permission to use this feature.";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        // Network error
        errorMessage =
          "Network error. Please check your connection and try again.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsGenerating(false);
      clearInterval(progressInterval);
    }
  };

  const handleTableSelection = (tableIndex, checked) => {
    setSelectedTables((prev) => ({
      ...prev,
      [tableIndex]: checked,
    }));
  };

  const handleEditField = (tableIndex, fieldIndex) => {
    if (
      !suggestedTables[tableIndex] ||
      !suggestedTables[tableIndex].Fields ||
      !suggestedTables[tableIndex].Fields[fieldIndex]
    ) {
      toast.error("Field not found", {
        position: "top-right",
        autoClose: 2000,
      });
      return;
    }

    const field = suggestedTables[tableIndex].Fields[fieldIndex];
    setCurrentEditField({
      tableIndex,
      fieldIndex,
      Field_Name: field.Field_Name || "",
      Field_Type: field.Field_Type || "String",
      description: field.description || "",
    });
    setFieldEditDialog(true);
  };

  const handleSaveFieldEdit = () => {
    if (!currentEditField.Field_Name.trim()) {
      toast.error("Field name is required", {
        position: "top-right",
        autoClose: 2000,
      });
      return;
    }

    const updatedTables = [...suggestedTables];
    if (
      updatedTables[currentEditField.tableIndex] &&
      updatedTables[currentEditField.tableIndex].Fields
    ) {
      updatedTables[currentEditField.tableIndex].Fields[
        currentEditField.fieldIndex
      ] = {
        Field_Name: currentEditField.Field_Name,
        Field_Type: currentEditField.Field_Type,
        description: currentEditField.description,
      };
      setSuggestedTables(updatedTables);
      setFieldEditDialog(false);
      setCurrentEditField({});

      toast.success("Field updated successfully!", {
        position: "top-right",
        autoClose: 2000,
      });
    }
  };

  const handleDeleteField = (tableIndex, fieldIndex) => {
    if (!suggestedTables[tableIndex] || !suggestedTables[tableIndex].Fields) {
      return;
    }

    const updatedTables = [...suggestedTables];
    const fieldName =
      updatedTables[tableIndex].Fields[fieldIndex]?.Field_Name ||
      "Unknown Field";
    updatedTables[tableIndex].Fields.splice(fieldIndex, 1);
    setSuggestedTables(updatedTables);

    toast.info(`Field "${fieldName}" deleted`, {
      position: "top-right",
      autoClose: 2000,
    });
  };

  const handleAddField = (tableIndex) => {
    if (!suggestedTables[tableIndex]) {
      return;
    }

    const updatedTables = [...suggestedTables];
    if (!updatedTables[tableIndex].Fields) {
      updatedTables[tableIndex].Fields = [];
    }

    updatedTables[tableIndex].Fields.push({
      Field_Name: "NewField",
      Field_Type: "String",
      description: "New field description",
    });
    setSuggestedTables(updatedTables);

    toast.success("New field added. Don't forget to edit its properties.", {
      position: "top-right",
      autoClose: 3000,
    });
  };

  // const handleAddSelectedTables = () => {
  //   const selectedTableData = suggestedTables.filter(
  //     (_, index) => selectedTables[index]
  //   );

  //   if (selectedTableData.length === 0) {
  //     toast.warning("Please select at least one table to proceed", {
  //       position: "top-right",
  //       autoClose: 3000,
  //     });
  //     return;
  //   }

  //   // Convert to the format expected by your backend
  //   const formattedTables = selectedTableData.map((table) => ({
  //     tableName: table.Table_Name || "Unnamed Table",
  //     description: table.description || "No description provided",
  //     fields: (table.Fields || []).map((field) => ({
  //       field_name: field.Field_Name || "Unnamed Field",
  //       field_type: field.Field_Type || "String",
  //       description: field.description || "No description provided",
  //       required: true, // You can make this configurable
  //     })),
  //   }));

  //   if (onModelGenerated) {
  //     onModelGenerated(formattedTables);
  //   }

  //   toast.success(`${selectedTableData.length} table(s) will be created!`, {
  //     position: "top-right",
  //     autoClose: 3000,
  //   });
  // };

  const handleAddSelectedTables = async () => {
    const selectedTableData = suggestedTables.filter(
      (_, index) => selectedTables[index]
    );

    if (selectedTableData.length === 0) {
      toast.warning("Please select at least one table to proceed", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      // Get user token from localStorage
      const userInfo = localStorage.getItem("userInfo");
      if (!userInfo) {
        throw new Error("User not authenticated. Please login again.");
      }

      const user = JSON.parse(userInfo);
      const token = user.token;

      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      // Get project ID from the project prop
      const projectId = project?._id || project?.id || project;
      if (!projectId) {
        throw new Error("Project ID not found.");
      }

      // Format data according to API specification
      const payload = {
        projectId: projectId,
        models: selectedTableData.map((table) => ({
          table_name: table.Table_Name || "Unnamed Table",
          is_user_table: false, // You can make this configurable based on table type
          fields: (table.Fields || []).map((field) => ({
            field_name: field.Field_Name || "Unnamed Field",
            field_type: field.Field_Type || "String",
          })),
          referenceTables: [], // You can populate this if needed for reference fields
        })),
      };

      console.log("Creating tables with payload:", payload);

      // Make API call to create tables
      const response = await axios.post(
        `${API_HOST}customroutes/multiple`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Tables created successfully:", response.data);

      // Call the onModelGenerated callback if provided (for parent component)
      if (onModelGenerated) {
        onModelGenerated(selectedTableData);
      }

      toast.success(
        `${selectedTableData.length} table(s) created successfully!`,
        {
          position: "top-right",
          autoClose: 3000,
        }
      );

      // Optional: Reset the component state after successful creation
      setSuggestedTables([]);
      setSelectedTables({});
      setPrompt("");
      dispatch(getAllModels(project));
    } catch (error) {
      console.error("Error creating tables:", error);

      let errorMessage = "Failed to create tables. Please try again.";

      if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          errorMessage = "Authentication failed. Please login again.";
        } else if (error.response.status === 403) {
          errorMessage =
            "Access denied. You don't have permission to create tables.";
        } else if (error.response.status === 400) {
          errorMessage = "Invalid data format. Please check your selections.";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        // Network error
        errorMessage =
          "Network error. Please check your connection and try again.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const getSelectedCount = () => {
    return Object.values(selectedTables || {}).filter(Boolean).length;
  };

  useEffect(() => {
    if (prompt && prompt.length > 5) {
      const lowercasePrompt = prompt.toLowerCase();
      const newSuggestions = [];

      if (
        lowercasePrompt.includes("user") ||
        lowercasePrompt.includes("profile")
      ) {
        newSuggestions.push("Add authentication and user management tables");
      }
      if (
        lowercasePrompt.includes("product") ||
        lowercasePrompt.includes("item")
      ) {
        newSuggestions.push("Include inventory and catalog management");
      }
      if (
        lowercasePrompt.includes("order") ||
        lowercasePrompt.includes("purchase")
      ) {
        newSuggestions.push("Add payment and order tracking tables");
      }
      if (
        lowercasePrompt.includes("class") ||
        lowercasePrompt.includes("course")
      ) {
        newSuggestions.push("Include scheduling and enrollment tables");
      }
      if (
        lowercasePrompt.includes("booking") ||
        lowercasePrompt.includes("reservation")
      ) {
        newSuggestions.push("Add booking and availability management");
      }

      setSuggestions(newSuggestions);
    } else {
      setSuggestions([]);
    }
  }, [prompt]);

  return (
    <Grid container spacing={3} sx={{ height: "60vh" }}>
      {/* Input Section */}
      <Grid item xs={12} md={6} sx={{ height: "100%" }}>
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "1.5rem",
            boxShadow: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Mont-SemiBold",
              fontSize: "1.2rem",
              mb: 2,
              color: "#333",
              flexShrink: 0,
            }}
          >
            Describe your application and its scope
          </Typography>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: "Mont-Regular",
                fontSize: "0.8rem",
                color: "#666",
                flexShrink: 0,
              }}
            >
              Provide a detailed description of the system you want to build. AI
              will analyze your requirements and suggest appropriate database
              tables with fields.
            </Typography>

            <Typography
              sx={{
                fontFamily: "Mont-Regular",
                fontSize: "0.6rem",
                color: "#666",
                flexShrink: 0,
              }}
            >
              <span className="text-red-600 ">*</span>Note: Please do not use
              any names of specific entities like companies, organizations,
              teams, projects, people, technologies or copyright terms.
            </Typography>

            <TextField
              multiline
              rows={8}
              placeholder="Example: I need a swimming class management system that handles student registrations, payments, class scheduling, coach assignments, token-based enrollment, invoice generation, and automated notifications for students about class availability and token expiry."
              variant="outlined"
              sx={{
                width: "100%",
                flexShrink: 0,
                "& .MuiOutlinedInput-root": {
                  fontFamily: "Mont-Regular",
                },
              }}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />

            {suggestions && suggestions.length > 0 && (
              <Box sx={{ flexShrink: 0 }}>
                <Typography
                  sx={{
                    fontFamily: "Mont-SemiBold",
                    fontSize: "0.9rem",
                    mb: 1,
                    color: "#333",
                  }}
                >
                  Suggestions to enhance your description:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {suggestions.map((suggestion, index) => (
                    <Chip
                      key={index}
                      label={suggestion}
                      onClick={() => setPrompt(prompt + "\n\n" + suggestion)}
                      sx={{
                        fontFamily: "Mont-Regular",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#f5f5f5" },
                      }}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ flexShrink: 0 }}>
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !prompt.trim()}
                variant="contained"
                fullWidth
                sx={{
                  fontFamily: "Mont-Regular",
                  backgroundColor: "red",
                  "&:hover": { color: "red", backgroundColor: "white" },
                  transitionDuration: "0.5s",
                  py: 1.5,
                }}
              >
                {isGenerating
                  ? `Generating Data-Models... ${generationProgress}%`
                  : "Generate Data-Models"}
              </Button>

              {isGenerating && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={generationProgress}
                    sx={{
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "red",
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "Mont-Regular",
                      fontSize: "0.8rem",
                      color: "#666",
                      textAlign: "center",
                      mt: 1,
                    }}
                  >
                    AI is analyzing your requirements and generating database
                    structure...
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Grid>

      {/* Output Section */}
      <Grid item xs={12} md={6} sx={{ height: "100%" }}>
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "1.5rem",
            boxShadow: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Mont-SemiBold",
              fontSize: "1.2rem",
              mb: 2,
              color: "#333",
              flexShrink: 0,
            }}
          >
            Generated Data-Models
          </Typography>

          {suggestedTables && suggestedTables.length > 0 ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ fontFamily: "Mont-SemiBold", mb: 1 }}>
                  Selection Summary
                </Typography>
                <Typography
                  sx={{ fontFamily: "Mont-Regular", fontSize: "0.9rem" }}
                >
                  {getSelectedCount()} of {suggestedTables.length} tables
                  selected
                </Typography>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  mb: 2,
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#c1c1c1",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#a8a8a8",
                  },
                }}
              >
                {suggestedTables.map((table, tableIndex) => {
                  const tableFields = table.Fields || [];
                  return (
                    <Accordion key={tableIndex} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedTables[tableIndex] || false}
                                onChange={(e) =>
                                  handleTableSelection(
                                    tableIndex,
                                    e.target.checked
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                            }
                            label=""
                            sx={{ mr: 1 }}
                          />
                          <Box>
                            <Typography sx={{ fontFamily: "Mont-SemiBold" }}>
                              {table.Table_Name || "Unnamed Table"}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Mont-Regular",
                                fontSize: "0.8rem",
                                color: "#666",
                              }}
                            >
                              {table.description || "No description provided"}
                            </Typography>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Mont-SemiBold",
                                fontSize: "1rem",
                              }}
                            >
                              Fields ({tableFields.length})
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleAddField(tableIndex)}
                              sx={{ color: "red" }}
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>

                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                                  <TableCell
                                    sx={{ fontFamily: "Mont-SemiBold" }}
                                  >
                                    Field Name
                                  </TableCell>
                                  <TableCell
                                    sx={{ fontFamily: "Mont-SemiBold" }}
                                  >
                                    Type
                                  </TableCell>
                                  <TableCell
                                    sx={{ fontFamily: "Mont-SemiBold" }}
                                  >
                                    Description
                                  </TableCell>
                                  <TableCell
                                    sx={{ fontFamily: "Mont-SemiBold" }}
                                  >
                                    Actions
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {tableFields.map((field, fieldIndex) => (
                                  <TableRow key={fieldIndex}>
                                    <TableCell
                                      sx={{ fontFamily: "Mont-Regular" }}
                                    >
                                      {field.Field_Name || "Unnamed Field"}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={field.Field_Type || "String"}
                                        size="small"
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontFamily: "Mont-Regular",
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      {field.description ||
                                        "No description provided"}
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ display: "flex", gap: 1 }}>
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleEditField(
                                              tableIndex,
                                              fieldIndex
                                            )
                                          }
                                          sx={{ color: "blue" }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleDeleteField(
                                              tableIndex,
                                              fieldIndex
                                            )
                                          }
                                          sx={{ color: "red" }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>

              <Button
                onClick={handleAddSelectedTables}
                variant="contained"
                fullWidth
                disabled={getSelectedCount() === 0}
                sx={{
                  fontFamily: "Mont-Regular",
                  backgroundColor: "red",
                  "&:hover": {
                    color: "red",
                    backgroundColor: "white",
                  },
                  py: 1.5,
                  flexShrink: 0,
                }}
              >
                Add Selected Tables ({getSelectedCount()})
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Mont-Regular",
                  color: "#999",
                  fontSize: "2rem",
                  mb: 2,
                }}
              >
                <StorageIcon sx={{ fontSize: "3rem", color: "#999" }} />
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Mont-Regular",
                  color: "#666",
                  fontSize: "0.9rem",
                }}
              >
                Describe your system requirements and AI will suggest the
                optimal database structure with tables and fields tailored to
                your needs.
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>

      {/* Field Edit Dialog */}
      <Dialog
        open={fieldEditDialog}
        onClose={() => setFieldEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "Mont-SemiBold" }}>
          Edit Field
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Field Name"
            value={currentEditField.Field_Name || ""}
            onChange={(e) =>
              setCurrentEditField((prev) => ({
                ...prev,
                Field_Name: e.target.value,
              }))
            }
            sx={{ mb: 2, mt: 1 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Field Type</InputLabel>
            <Select
              value={currentEditField.Field_Type || "String"}
              label="Field Type"
              onChange={(e) =>
                setCurrentEditField((prev) => ({
                  ...prev,
                  Field_Type: e.target.value,
                }))
              }
            >
              <MenuItem value="String">String</MenuItem>
              <MenuItem value="Number">Number</MenuItem>
              <MenuItem value="Boolean">Boolean</MenuItem>
              <MenuItem value="Date-Time">Date-Time</MenuItem>
              <MenuItem value="Time">Time</MenuItem>
              <MenuItem value="File">File</MenuItem>
              <MenuItem value="Reference">Reference</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            value={currentEditField.description || ""}
            onChange={(e) =>
              setCurrentEditField((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldEditDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveFieldEdit}
            variant="contained"
            sx={{
              backgroundColor: "red",
              "&:hover": { color: "red", backgroundColor: "white" },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default AIModelGenerator;
